'use client'
import { useSearchParams } from 'next/navigation'
import { type Dispatch, type SetStateAction, useState } from 'react'

import { useExperimentsPageType } from '../../../experiments/components/ExperimentsPageTypeContext'
import { productSearchOptimizeSearchQueryIfItHasPzns } from '../../../product-search'

/**
 * Custom hook for managing the search query state based on URL parameters and minimum query length.
 * @param minQueryLength - The minimum length of the query required to initialize the state.
 *
 * @returns A tuple containing:
 * - `searchQuery`: The current search query string.
 * - `setSearchQuery`: A function to update the search query state.
 *
 * @remarks
 * - If the `pageType` is 'product', the search query will be initialized as an empty string.
 * - If the `pageType` is not 'product', the hook will attempt to retrieve the query from the URL parameters (`query` or `q`).
 * - If the retrieved query length is less than the specified `minQueryLength`, the search query will be initialized as an empty string.
 */
export const useSearchQuery = (
  minQueryLength: number,
): [string, Dispatch<SetStateAction<string>>] => {
  const pageType = useExperimentsPageType()
  const searchParams = useSearchParams()
  const urlSearchQuery =
    pageType === 'product'
      ? ''
      : (searchParams.get('query') ?? searchParams.get('q'))
  const { searchQuery } =
    productSearchOptimizeSearchQueryIfItHasPzns(urlSearchQuery)

  return useState(
    searchQuery && searchQuery.length >= minQueryLength ? searchQuery : '',
  )
}
